import { useState, useEffect, useCallback } from 'react';
import type { User, AuthState } from '../types';
import { getCurrentUser, logout as apiLogout } from '../utils/api';

const AUTH_TOKEN_KEY = 'auth_token';

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Check for existing auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      
      if (!token) {
        setState({ user: null, isAuthenticated: false, isLoading: false });
        return;
      }

      try {
        const response = await getCurrentUser();
        if (response.success && response.data?.user) {
          setState({
            user: response.data.user,
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          // Invalid token - only clear if we got a definitive "unauthorized" response
          console.warn('Auth check returned unsuccessful:', response);
          localStorage.removeItem(AUTH_TOKEN_KEY);
          setState({ user: null, isAuthenticated: false, isLoading: false });
        }
      } catch (error) {
        // Don't clear token on network errors - only log and keep existing state
        // This prevents logout during temporary network issues or when popup is open
        console.error('Auth check failed (keeping token):', error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        
        // Only clear token if it's explicitly invalid (401/403 with specific messages)
        if (errorMessage.includes('No token') || errorMessage.includes('Invalid token') || errorMessage.includes('expired')) {
          localStorage.removeItem(AUTH_TOKEN_KEY);
          setState({ user: null, isAuthenticated: false, isLoading: false });
        } else {
          // Keep the user logged in for transient errors
          setState(prev => ({ ...prev, isLoading: false }));
        }
      }
    };

    checkAuth();
  }, []);

  const login = useCallback((user: User, token: string) => {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    setState({
      user,
      isAuthenticated: true,
      isLoading: false,
    });
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  const updateUser = useCallback((updates: Partial<User>) => {
    setState(prev => ({
      ...prev,
      user: prev.user ? { ...prev.user, ...updates } : null,
    }));
  }, []);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;

    try {
      const response = await getCurrentUser();
      if (response.success && response.data?.user) {
        setState({
          user: response.data.user,
          isAuthenticated: true,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  return {
    ...state,
    login,
    logout,
    updateUser,
    refreshUser,
  };
}
